import type { ChatMessage } from "./api";
import { api } from "./api";
import type { Msg } from "./sessions";

/** Trigger compression when estimated or measured usage exceeds this share of num_ctx */
export const COMPRESS_AT = 0.78;
/** Keep this many newest messages verbatim after compression */
export const KEEP_RECENT = 6;

export function estimateTokens(text: string): number {
  return Math.ceil((text || "").length / 4);
}

export function estimateMessagesTokens(msgs: { content: string }[]): number {
  return msgs.reduce((n, m) => n + estimateTokens(m.content) + 4, 0);
}

function formatDialog(msgs: Msg[]): string {
  return msgs
    .map((m) => {
      const role = m.role === "user" ? "User" : "Assistant";
      const body = m.content.trim();
      return body ? `${role}:\n${body}` : null;
    })
    .filter(Boolean)
    .join("\n\n");
}

/** Cap text so the compress prompt itself fits into num_ctx */
function truncateForBudget(text: string, maxTokens: number): string {
  const maxChars = Math.max(2000, maxTokens * 4);
  if (text.length <= maxChars) return text;
  const head = Math.floor(maxChars * 0.55);
  const tail = Math.floor(maxChars * 0.35);
  return (
    text.slice(0, head) +
    `\n\n[…обрезано ${text.length - head - tail} символов…]\n\n` +
    text.slice(-tail)
  );
}

/** Offline fallback when the model returns empty / errors */
function extractiveSummary(msgs: Msg[], prevSummary?: string): string {
  const parts: string[] = [];
  if (prevSummary?.trim()) {
    parts.push("Предыдущее резюме:\n" + prevSummary.trim().slice(0, 4000));
  }
  for (const m of msgs) {
    const role = m.role === "user" ? "User" : "Assistant";
    let body = m.content.replace(/\s+/g, " ").trim();
    if (!body) continue;
    if (body.length > 500) body = body.slice(0, 420) + "…";
    parts.push(`${role}: ${body}`);
  }
  let out = parts.join("\n");
  if (out.length > 8000) out = out.slice(0, 7800) + "…";
  return out || "Краткий контекст недоступен.";
}

export function buildApiMessages(
  history: Msg[],
  summary?: string,
  summarizedCount = 0
): ChatMessage[] {
  const tail = history.slice(Math.max(0, summarizedCount));
  const out: ChatMessage[] = [];
  if (summary?.trim()) {
    out.push({
      role: "system",
      content:
        "Ниже — сжатый контекст более ранней части диалога. Опирайся на него как на факты из истории, " +
        "но приоритет у последних сообщений пользователя.\n\n" +
        summary.trim(),
    });
  }
  for (const m of tail) {
    out.push({ role: m.role, content: m.content });
  }
  return out;
}

export function shouldCompress(opts: {
  history: Msg[];
  summary?: string;
  summarizedCount?: number;
  numCtx: number;
  ctxUsed: number | null;
  reservePredict: number;
}): boolean {
  const { history, summary, summarizedCount = 0, numCtx, ctxUsed, reservePredict } = opts;
  const live = history.slice(Math.max(0, summarizedCount));
  if (live.length <= KEEP_RECENT + 1) return false;

  const budget = Math.max(1024, numCtx - Math.min(reservePredict, Math.floor(numCtx * 0.35)));
  const est = estimateTokens(summary || "") + estimateMessagesTokens(live) + 64;

  if (ctxUsed != null && ctxUsed / numCtx >= COMPRESS_AT) return true;
  if (est / budget >= COMPRESS_AT) return true;
  return false;
}

async function completeOnce(
  body: {
    model: string;
    messages: ChatMessage[];
    options?: { num_predict?: number; num_ctx?: number };
  },
  signal?: AbortSignal
): Promise<string> {
  let content = "";
  let streamError: string | null = null;

  await api.chatStream(
    { ...body, think: false },
    {
      signal,
      onDelta: (d) => {
        content += d.content || "";
      },
      onDone: () => {},
      onError: (msg) => {
        streamError = msg;
      },
    }
  );

  if (streamError) throw new Error(streamError);

  // Only use thinking field when think is enabled server-side; content is already filtered
  return content.trim();
}

export async function compressContext(opts: {
  model: string;
  history: Msg[];
  summary?: string;
  summarizedCount?: number;
  numCtx: number;
  signal?: AbortSignal;
}): Promise<{ summary: string; summarizedCount: number }> {
  const { model, history, numCtx, signal } = opts;
  const prevCount = opts.summarizedCount || 0;
  const keep = Math.min(KEEP_RECENT, Math.max(2, history.length - 2));
  if (history.length <= keep) {
    return { summary: opts.summary || "", summarizedCount: prevCount };
  }

  const cutAt = history.length - keep;
  const chunk = history.slice(prevCount, cutAt);
  if (!chunk.length) {
    return { summary: opts.summary || "", summarizedCount: prevCount };
  }

  const prev = (opts.summary || "").trim();
  const dialogFull = formatDialog(chunk);
  // Leave room for instructions + output inside compress window
  const compressCtx = Math.min(Math.max(numCtx, 8192), 32768);
  const dialog = truncateForBudget(dialogFull, Math.floor(compressCtx * 0.55));

  const userPrompt = prev
    ? `У тебя уже есть сжатое резюме более раннего диалога.\nОбнови его, включив новые фрагменты ниже. ` +
      `Сохрани важные факты, решения, имена файлов/функций, ограничения и открытые вопросы. ` +
      `Убери повторы и воду. Пиши компактно, на языке диалога.\n\n` +
      `### Текущее резюме\n${truncateForBudget(prev, Math.floor(compressCtx * 0.15))}\n\n` +
      `### Новые сообщения\n${dialog}\n\n### Обновлённое резюме`
    : `Составь компактное резюме диалога ниже для передачи другой модели как system-контекст. ` +
      `Сохрани важные факты, решения, имена файлов/функций, ограничения и открытые вопросы. ` +
      `Убери воду. На языке диалога.\n\n### Диалог\n${dialog}\n\n### Резюме`;

  let summary = "";
  try {
    summary = await completeOnce(
      {
        model,
        messages: [
          {
            role: "system",
            content:
              "Ты сжимаешь контекст чата. Отвечай только текстом резюме, без преамбулы, без тегов thinking и без кавычек.",
          },
          { role: "user", content: userPrompt },
        ],
        options: {
          num_predict: Math.min(2048, Math.max(512, Math.floor(compressCtx / 8))),
          num_ctx: compressCtx,
        },
      },
      signal
    );
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    // fall through to extractive
    summary = "";
  }

  if (!summary) {
    summary = extractiveSummary(chunk, prev);
  }

  return { summary, summarizedCount: cutAt };
}
